import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, EmptyState, ErrorAlert } from '../components/ui';
import * as publicApi from '../api/public';
import { ui, cn } from '../lib/ui';

export default function PublicEvents() {
  const { orgSlug } = useParams();

  const [org, setOrg] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const id = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const [orgData, eventsData] = await Promise.all([
        publicApi.getOrgProfile(orgSlug),
        publicApi.listOrgEvents(orgSlug, { limit: 50 }),
      ]);
      if (id !== runIdRef.current) return;
      setOrg(orgData);
      setEvents(eventsData.events || []);
    } catch (err) {
      if (id !== runIdRef.current) return;
      setError(err.message || 'Failed to load events');
    } finally {
      if (id === runIdRef.current) setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className={ui.publicPage}>
        <Spinner />
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className={ui.publicPage}>
        <div className={ui.publicContainer}>
          <ErrorAlert message={error || 'Organization not found'} />
        </div>
      </div>
    );
  }

  return (
    <div className={ui.publicPage}>
      <header className={ui.publicHeader}>
        <div className={ui.publicContainer}>
          <div className="flex items-center gap-3">
            <Link
              to={`/o/${orgSlug}`}
              className="text-inherit no-underline hover:no-underline"
            >
              {org.logoUrl && (
                <img
                  src={org.logoUrl}
                  alt={`${org.name} logo`}
                  className={cn(ui.publicOrgLogo, 'size-9!')}
                />
              )}
            </Link>
            <Link
              to={`/o/${orgSlug}`}
              className="font-semibold text-inherit no-underline hover:no-underline"
            >
              {org.name}
            </Link>
          </div>
        </div>
      </header>

      <main className={cn(ui.publicContainer, 'pt-8')}>
        <h1 className={ui.h1}>Events</h1>

        {events.length === 0 && (
          <EmptyState message="No public events available at this time." />
        )}

        {events.length > 0 && (
          <div className={cn(ui.grid, ui.gridMd2)}>
            {events.map(evt => (
              <Link
                key={evt.eventId}
                to={`/o/${orgSlug}/events/${evt.eventId}`}
                className={cn(ui.card, ui.publicEventCard, 'text-inherit no-underline hover:no-underline')}
              >
                <h3 className="mt-0 mb-2 text-title-s">{evt.name}</h3>
                {evt.startsAt && (
                  <p className={cn('m-0 text-body-s', ui.muted)}>
                    {new Date(evt.startsAt).toLocaleDateString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                )}
                {evt.venue && (
                  <p className="mt-1 mb-0 text-body-s">
                    {evt.venue}
                  </p>
                )}
                {evt.description && (
                  <p
                    className={cn('mt-2 mb-0 line-clamp-2 text-body-s', ui.muted)}
                  >
                    {evt.description}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
